import React, { useState, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../firebase.init";
import toast from "react-hot-toast";

const UserProfileUpdate = ({ userData }) => {
  const [user] = useAuthState(auth);
  const [profileImage, setProfileImage] = useState("");
  const [formData, setFormData] = useState({
    userName: "",
    aboutMe: "",
    profileURL: "",
    email: "",
    address: "",
    city: "",
    country: "",
    phone: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Pre-populate form with existing user data
    if (userData) {
      setFormData({
        userName: userData.userName || user?.displayName || "",
        aboutMe: userData.aboutMe || "",
        profileURL: userData.profileURL || user?.photoURL || "",
        email: userData.email || user?.email || "",
        address: userData.address || "",
        city: userData.city || "",
        country: userData.country || "",
        phone: userData.phone || "",
      });
      setProfileImage(userData.profileURL || user?.photoURL || "");
    }
  }, [userData, user]);

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File size exceeds 2 MB. Please upload a smaller image.");
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await fetch(
        "https://server.enjoywiki.com/image-server/upload",
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();
      if (data.file && data.file.url) {
        setProfileImage(data.file.url);
        setFormData((prev) => ({ ...prev, profileURL: data.file.url }));
        toast.success("Image uploaded successfully!");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Use the actual user ID from the fetched userData
      const userId = userData._id;
      const response = await fetch(
        `https://allyoutube-01-02d59d78beb5.herokuapp.com/update-user-profile/${userId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (response.ok) {
        toast.success("Profile updated successfully!");
      } else {
        toast.error(
          "Failed to update profile: " + (result.message || "Unknown error")
        );
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("An error occurred while updating your profile.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h3 className="mb-4 border-bottom pb-3 text-white">Account Settings</h3>

      <div className="row">
        {/* Profile Image Section */}
        <div className="col-md-4 mb-4">
          <div className="card shadow-sm">
            <div className="card-body text-center">
              <h5 className="card-title mb-3">Profile Photo</h5>
              <div className="mb-3">
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="Profile"
                    className="rounded-circle img-fluid mb-3"
                    style={{
                      width: "150px",
                      height: "150px",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    className="bg-light rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                    style={{ width: "150px", height: "150px" }}
                  >
                    <span className="text-muted fs-1">?</span>
                  </div>
                )}
                <div className="d-grid">
                  <label
                    className="btn btn-outline-primary mb-2"
                    htmlFor="profileImage"
                  >
                    {isLoading ? "Uploading..." : "Change Photo"}
                  </label>
                  <input
                    type="file"
                    id="profileImage"
                    accept="image/*"
                    className="d-none"
                    onChange={handleImageUpload}
                    disabled={isLoading}
                  />
                  {profileImage && (
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => {
                        setProfileImage("");
                        setFormData((prev) => ({ ...prev, profileURL: "" }));
                      }}
                      disabled={isLoading}
                      type="button"
                    >
                      Remove Photo
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Personal Information Section */}
        <div className="col-md-8">
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h5 className="card-title mb-3">Personal Information</h5>

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="userName" className="form-label text-dark text-muted">
                    Full Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="userName"
                    value={formData.userName}
                    onChange={handleChange}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="email" className="form-label text-dark text-muted">
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    disabled
                    value={formData.email}
                    onChange={handleChange}
                    readOnly
                  />
                  <small className="text-muted">
                    Email address cannot be changed
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="aboutMe" className="form-label text-dark text-muted">
                    About Me
                  </label>
                  <textarea
                    className="form-control"
                    id="aboutMe"
                    rows="3"
                    value={formData.aboutMe}
                    onChange={handleChange}
                    placeholder="Tell us a little about yourself"
                  ></textarea>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label htmlFor="address" className="form-label text-dark text-muted">
                      Address
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Your address"
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="phone" className="form-label text-dark text-muted">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Your phone number"
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label htmlFor="city" className="form-label text-dark text-muted">
                      City
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Your city"
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="country" className="form-label text-dark text-muted">
                      Country
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="country"
                      value={formData.country}
                      onChange={handleChange}
                      placeholder="Your country"
                    />
                  </div>
                </div>

                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileUpdate;
